import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/lib/supabase";

interface Props {
  meetingId: string;
  hasTranscript: boolean;
}

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  created_at: string;
}

export function MeetingChat({ meetingId, hasTranscript }: Props) {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    supabase
      .from("chat_messages")
      .select("id, role, content, created_at")
      .eq("meeting_id", meetingId)
      .order("created_at", { ascending: true })
      .then(({ data }) => {
        if (cancelled) return;
        setMessages((data as ChatMessage[]) ?? []);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [meetingId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages.length]);

  const send = async () => {
    const question = input.trim();
    if (!question || sending) return;

    setSending(true);
    setError(null);
    setInput("");

    const optimisticUser: ChatMessage = {
      id: `tmp-${Date.now()}`,
      role: "user",
      content: question,
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, optimisticUser]);

    try {
      const { data, error: invokeError } = await supabase.functions.invoke(
        "ask-meeting",
        { body: { meeting_id: meetingId, question } }
      );
      if (invokeError) throw new Error(invokeError.message);
      const inserted = (data?.messages ?? []) as ChatMessage[];
      if (inserted.length === 2) {
        setMessages((m) => [
          ...m.filter((x) => x.id !== optimisticUser.id),
          ...inserted,
        ]);
      } else if (data?.answer) {
        setMessages((m) => [
          ...m,
          {
            id: `a-${Date.now()}`,
            role: "assistant",
            content: data.answer,
            created_at: new Date().toISOString(),
          },
        ]);
      }
    } catch (e) {
      setMessages((m) => m.filter((x) => x.id !== optimisticUser.id));
      setInput(question);
      setError((e as Error).message);
    } finally {
      setSending(false);
    }
  };

  if (!hasTranscript) return null;

  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <svg className="w-5 h-5 text-redi-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
        <h3 className="text-redi-text text-lg font-semibold">
          {t("chat.title")}
        </h3>
      </div>

      <div
        ref={scrollRef}
        className="bg-redi-surface rounded-xl p-4 mb-3 max-h-[400px] overflow-y-auto"
      >
        {loading ? (
          <div className="flex justify-center py-4">
            <div className="w-5 h-5 border-2 border-redi-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <p className="text-redi-muted text-sm text-center py-2">
            {t("chat.placeholder")}
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {messages.map((m) => (
              <div
                key={m.id}
                className={`px-4 py-3 rounded-2xl max-w-[90%] whitespace-pre-wrap text-sm leading-6 ${
                  m.role === "user"
                    ? "bg-redi-primary text-white self-end"
                    : "bg-redi-dark text-redi-text self-start"
                }`}
              >
                {m.content}
              </div>
            ))}
          </div>
        )}
      </div>

      {error && (
        <p className="text-redi-error text-sm mb-2">{error}</p>
      )}

      <div className="flex items-end gap-2 bg-redi-surface rounded-xl p-2">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder={t("chat.inputPlaceholder")}
          rows={1}
          maxLength={2000}
          disabled={sending}
          className="flex-1 bg-transparent text-redi-text text-sm px-2 py-2 resize-none focus:outline-none disabled:opacity-50"
          style={{ maxHeight: 120 }}
        />
        <button
          onClick={send}
          disabled={sending || !input.trim()}
          className="w-10 h-10 rounded-full bg-redi-primary text-white flex items-center justify-center hover:bg-redi-primary/80 disabled:opacity-50"
          aria-label={t("chat.send")}
        >
          {sending ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M2 21l21-9L2 3v7l15 2-15 2v7z" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}
